<script lang="ts">
import { computed, defineComponent, h, nextTick, onUpdated, ref, unref, watch } from 'vue';

import { classNames, debounce, filterEmpty } from '@components/utils';
import { useRender } from '@components/composable';

import { CdsIcon } from '../CdsIcon';

import CdsTree from './CdsTree.vue';
import {
  calcRangeKeys,
  conductExpandParent,
  convertDataToEntities,
  convertDirectoryKeysToNodes,
  convertTreeToData,
  fillFieldNames
} from './utils';
import { makeTreeProps } from './composable';

import type { NTree } from './interface';
import type { ITreeProps } from './composable';
import type { ScrollTo } from './components/VirtualList';

function getIcon(props: NTree.ITreeNodeAttribute) {
  const { isLeaf, expanded } = props;
  if (isLeaf) {
    return h(CdsIcon, { name: 'doc' });
  }

  return expanded ? h(CdsIcon, { name: 'directory-open' }) : h(CdsIcon, { name: 'directory' });
}

export const componentName = 'CdsTreeDirectory';
export default defineComponent({
  name: componentName,
  inheritAttrs: false,
  props: {
    ...makeTreeProps({
      showIcon: true,
      expandAction: 'click'
    })
  },
  emits: {
    /**
     * Событие выбора узла (v-model)
     * @type { selectedKeys: NTree.TKey[] }
     */
    'update:selectedKeys': (selectedKeys: NTree.TKey[]) => true,
    /**
     * Событие выбора узла по чекбоксу (v-model)
     * @type { checkedKeys: NTree.TKey[] | { checked: NTree.TKey[]; halfChecked: NTree.TKey[] } }
     */
    'update:checkedKeys': (checkedKeys: NTree.TKey[] | { checked: NTree.TKey[]; halfChecked: NTree.TKey[] }) => true,
    /**
     * Событие раскрытия узла (v-model)
     * @type { expandedKeys: NTree.TKey[] }
     */
    'update:expandedKeys': (expandedKeys: NTree.TKey[]) => true,
    /**
     * Событие клика по узлу
     * @type { event: MouseEvent, node: NTree.IEventDataNode }
     */
    click: (event: MouseEvent, node: NTree.IEventDataNode) => true,
    /**
     * Событие выбора узла
     * @type { checked: { checked: NTree.TKey[]; halfChecked: NTree.TKey[] } | NTree.TKey[], info: NTree.ICheckInfo }
     */
    check: (checked: { checked: NTree.TKey[]; halfChecked: NTree.TKey[] } | NTree.TKey[], info: NTree.ICheckInfo) =>
      true,
    /**
     * Событие начала перетаскивания узла
     * @type { info: NTree.TNodeDragEventParams }
     */
    dragstart: (info: NTree.TNodeDragEventParams) => true,
    /**
     * Событие окончания перетаскивания узла
     * @type { info: NTree.TNodeDragEventParams }
     */
    dragend: (info: NTree.TNodeDragEventParams) => true,
    /**
     * Событие входа узла в зону перетаскивания
     * @type { info: NTree.TNodeDragEventParams & { expandedKeys: NTree.TKey[] } }
     */
    dragenter: (info: NTree.TNodeDragEventParams & { expandedKeys: NTree.TKey[] }) => true,
    /**
     * Событие выхода узла из зоны перетаскивания
     * @type { info: NTree.TNodeDragEventParams }
     */
    dragleave: (info: NTree.TNodeDragEventParams) => true,
    /**
     * Событие перемещения узла в зоне перетаскивания
     * @type { info: NTree.TNodeDragEventParams }
     */
    dragover: (info: NTree.TNodeDragEventParams) => true,
    /**
     * Событие перемещения узла
     * @type { info: NTree.TNodeDragEventParams & { dragNode: NTree.IEventDataNode; dragNodesKeys: NTree.TKey[]; dragPosition: number;dropToGap: boolean; } }
     */
    drop: (
      info: NTree.TNodeDragEventParams & {
        dragNode: NTree.IEventDataNode;
        dragNodesKeys: NTree.TKey[];
        dragPosition: number;
        dropToGap: boolean;
      }
    ) => true,
    /**
     * Событие раскрытия узла
     * @type { expandedKeys: NTree.TKey[], info: { node: NTree.TEventHandler; expanded: boolean; nativeEvent: MouseEvent } }
     */
    expand: (
      expandedKeys: NTree.TKey[],
      info: { node: NTree.IEventDataNode; expanded: boolean; nativeEvent: MouseEvent }
    ) => true,
    /**
     * Событие выбора узла
     * @type { selectedKeys: NTree.TKey, info: { event: 'select'; selected: boolean; node: NTree.IEventDataNode; selectedNodes: NTree.IDataNode[]; nativeEvent: MouseEvent; } }
     */
    select: (
      selectedKeys: NTree.TKey[],
      info: {
        event: 'select';
        selected: boolean;
        node: NTree.IEventDataNode;
        selectedNodes: NTree.IDataNode[];
        nativeEvent: MouseEvent;
      }
    ) => true,
    /**
     * Событие загрузки узла
     * @type { treeNode: NTree.IEventDataNode }
     */
    load: (treeNode: NTree.IEventDataNode) => true,
    /**
     * Событие правого клика по узлу
     * @type { info: { event: MouseEvent; node: NTree.IEventDataNode } }
     */
    rightClick: (info: { event: MouseEvent; node: NTree.IEventDataNode }) => true,

    dblclick: (event: MouseEvent, node: NTree.IEventDataNode) => true,
    doubleclick: (event: MouseEvent, node: NTree.IEventDataNode) => true
  },
  setup(props, { attrs, slots, emit, expose }) {
    const treeData = ref<NTree.IDataNode[] | undefined>(
      props.treeData || convertTreeToData(filterEmpty(slots.default?.()))
    );

    watch(
      () => props.treeData,
      () => {
        treeData.value = props.treeData;
      }
    );

    onUpdated(() => {
      nextTick(() => {
        if (props.treeData !== undefined && slots.default) {
          treeData.value = convertTreeToData(filterEmpty(slots.default?.()));
        }
      });
    });

    const lastSelectedKey = ref<NTree.TKey>();
    const cachedSelectedKeys = ref<NTree.TKey[]>();
    const fieldNames = computed(() => fillFieldNames(props.fieldNames));

    const treeRef = ref();

    const scrollTo: ScrollTo = (scroll) => {
      unref(treeRef)?.scrollTo(scroll);
    };

    const getInitExpandedKeys = () => {
      const { keyEntities } = convertDataToEntities(unref(treeData)!, {
        fieldNames: unref(fieldNames)
      });

      let initExpandedKeys: any;

      // Expanded keys
      if (props.defaultExpandAll) {
        initExpandedKeys = Object.keys(keyEntities);
      } else if (props.defaultExpandParent) {
        initExpandedKeys = conductExpandParent(props.expandedKeys || props.defaultExpandedKeys || [], keyEntities);
      } else {
        initExpandedKeys = props.expandedKeys || props.defaultExpandedKeys;
      }
      return initExpandedKeys;
    };

    const selectedKeys = ref(props.selectedKeys || props.defaultSelectedKeys || []);

    const expandedKeys = ref<NTree.TKey[]>(getInitExpandedKeys());

    watch(
      () => props.selectedKeys,
      () => {
        if (props.selectedKeys !== undefined) {
          selectedKeys.value = props.selectedKeys;
        }
      },
      { immediate: true }
    );

    watch(
      () => props.expandedKeys,
      () => {
        if (props.expandedKeys !== undefined) {
          expandedKeys.value = props.expandedKeys;
        }
      },
      { immediate: true }
    );

    const expandFolderNode = (event: MouseEvent, node: any) => {
      const { isLeaf } = node;

      if (isLeaf || event.shiftKey || event.metaKey || event.ctrlKey) {
        return;
      }
      unref(treeRef)!.onNodeExpand(event as any, node);
    };

    const onDebounceExpand = debounce(expandFolderNode, 200);

    const onExpand = (
      keys: NTree.TKey[],
      info: {
        node: NTree.IEventDataNode;
        expanded: boolean;
        nativeEvent: MouseEvent;
      }
    ) => {
      if (props.expandedKeys === undefined) {
        expandedKeys.value = keys;
      }
      // Call origin function
      emit('update:expandedKeys', keys);
      emit('expand', keys, info);
    };

    const onClick = (event: MouseEvent, node: NTree.IEventDataNode) => {
      const { expandAction } = props;

      // Expand the tree
      if (expandAction === 'click') {
        onDebounceExpand(event, node);
      }
      emit('click', event, node);
    };

    const onDoubleClick = (event: MouseEvent, node: NTree.IEventDataNode) => {
      const { expandAction } = props;
      // Expand the tree
      if (expandAction === 'dblclick' || expandAction === 'doubleclick') {
        onDebounceExpand(event, node);
      }

      emit('doubleclick', event, node);
      emit('dblclick', event, node);
    };

    const onSelect = (
      keys: NTree.TKey[],
      event: {
        event: 'select';
        selected: boolean;
        node: any;
        selectedNodes: NTree.IDataNode[];
        nativeEvent: MouseEvent;
      }
    ) => {
      const { node, nativeEvent } = event;
      const key = node[unref(fieldNames).key];

      const newEvent: any = {
        ...event,
        selected: true
      };

      // Windows / Mac single pick
      const ctrlPick: boolean = nativeEvent?.ctrlKey || nativeEvent?.metaKey;
      const shiftPick: boolean = nativeEvent?.shiftKey;

      // Generate new selected keys
      let newSelectedKeys: NTree.TKey[];
      if (props.multiple && ctrlPick) {
        // Control click
        newSelectedKeys = keys;
        lastSelectedKey.value = key;
        cachedSelectedKeys.value = newSelectedKeys;
        newEvent.selectedNodes = convertDirectoryKeysToNodes(unref(treeData)!, newSelectedKeys, unref(fieldNames));
      } else if (props.multiple && shiftPick) {
        // Shift click
        newSelectedKeys = Array.from(
          new Set([
            ...(unref(cachedSelectedKeys) || []),
            ...calcRangeKeys({
              treeData: unref(treeData)!,
              expandedKeys: unref(expandedKeys),
              startKey: key,
              endKey: unref(lastSelectedKey)!,
              fieldNames: unref(fieldNames)
            })
          ])
        );
        newEvent.selectedNodes = convertDirectoryKeysToNodes(unref(treeData)!, newSelectedKeys, unref(fieldNames));
      } else {
        // Single click
        newSelectedKeys = [key];
        lastSelectedKey.value = key;
        cachedSelectedKeys.value = newSelectedKeys;
        newEvent.selectedNodes = convertDirectoryKeysToNodes(unref(treeData)!, newSelectedKeys, unref(fieldNames));
      }

      emit('update:selectedKeys', newSelectedKeys);
      emit('select', newSelectedKeys, newEvent);

      if (props.selectedKeys === undefined) {
        selectedKeys.value = newSelectedKeys;
      }
    };

    const onCheck: ITreeProps['onCheck'] = (checkedObjOrKeys, eventObj) => {
      emit('update:checkedKeys', checkedObjOrKeys);
      emit('check', checkedObjOrKeys, eventObj);
    };

    useRender(() => {
      const connectClassName = classNames('cds-tree-directory', attrs.class);
      const { icon = slots.icon, blockNode = true, ...otherProps } = props;

      return h(
        CdsTree,
        {
          ...attrs,
          ref: treeRef,
          icon: icon || getIcon,
          blockNode,
          ...(otherProps as any), // @todo fix type
          selectable: true,
          class: connectClassName,
          expandedKeys: unref(expandedKeys),
          selectedKeys: unref(selectedKeys),
          onDblclick: onDoubleClick,
          onSelect,
          onClick,
          onExpand,
          onCheck
        },
        slots
      );
    });

    expose({
      scrollTo,
      selectedKeys: computed(() => unref(treeRef)?.selectedKeys),
      checkedKeys: computed(() => unref(treeRef)?.checkedKeys),
      halfCheckedKeys: computed(() => unref(treeRef)?.halfCheckedKeys),
      loadedKeys: computed(() => unref(treeRef)?.loadedKeys),
      loadingKeys: computed(() => unref(treeRef)?.loadingKeys),
      expandedKeys: computed(() => unref(treeRef)?.expandedKeys)
    });

    return {};
  }
});
</script>
